import React, { useState } from 'react';
import {
  Box, Chip, Grid, Table, Tooltip, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha,
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { GROWTH_METRICS, cmgr, momChange } from '../../utils/platformKpi';
import { SampleChip, Section, formatAed, formatBytes, formatCount } from './common';

const FORMAT = { count: formatCount, aed: formatAed, bytes: formatBytes };
const fmt = (metric, v) => FORMAT[metric.format](v);
const fmtDelta = (metric, d) => (d == null ? '—' : `${d > 0 ? '+' : d < 0 ? '−' : ''}${fmt(metric, Math.abs(d))}`);
const fmtPct = (p) => (p == null ? '—' : `${p > 0 ? '+' : ''}${p}%`);
const pctColor = (p) => (p == null || p === 0 ? 'text.secondary' : p > 0 ? 'success.main' : 'error.main');

const monthShort = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
const monthLabel = (m, short) => new Date(`${m}-01T00:00:00Z`)
  .toLocaleDateString('en-GB', { month: 'short', year: short ? '2-digit' : 'numeric', timeZone: 'UTC' });

const GrowthTile = ({ metric, series, months, selected, onClick }) => {
  const mom = momChange(series.values, metric.kind);
  const vsLabel = metric.kind === 'flow'
    ? `${monthShort(months[months.length - 2])} vs ${monthShort(months[months.length - 3])}`
    : `vs ${monthShort(months[months.length - 2])}`;
  const vsTooltip = metric.kind === 'flow'
    ? `${monthLabel(months[months.length - 2])} (complete) vs ${monthLabel(months[months.length - 3])}`
    : `Now vs end of ${monthLabel(months[months.length - 2])}`;
  return (
    <Box
      data-testid={`growth-tile-${metric.key}`}
      onClick={onClick}
      sx={{
        p: 1.5, borderRadius: 1, border: 1, height: '100%', cursor: 'pointer', minWidth: 0,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? (t) => alpha(t.palette.primary.main, 0.06) : undefined,
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, minWidth: 0 }}>
        <Tooltip title={metric.hint || ''}>
          <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 0 }}>{metric.label}</Typography>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        {series.source === 'sample' && <SampleChip />}
      </Box>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
        {fmt(metric, mom.current)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, whiteSpace: 'nowrap' }}>
        <Typography data-testid={`growth-mom-${metric.key}`} sx={{ fontSize: '0.8125rem', fontWeight: 700, color: pctColor(mom.pct) }}>
          {fmtPct(mom.pct)}
        </Typography>
        <Tooltip title={vsTooltip}>
          <Typography noWrap sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{vsLabel}</Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

/** Month-over-month growth across the platform's key numbers (design §3.11). */
const GrowthPanel = ({ growth }) => {
  const metrics = GROWTH_METRICS.filter((m) => growth?.series?.[m.key]);
  const [selectedKey, setSelectedKey] = useState(metrics[0]?.key);
  const [view, setView] = useState('total');
  if (!metrics.length) {
    return <Section title="Growth"><Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>No history yet.</Typography></Section>;
  }

  const { months } = growth;
  const metric = metrics.find((m) => m.key === selectedKey) || metrics[0];
  const series = growth.series[metric.key];
  const values = series.values;
  const isFlow = metric.kind === 'flow';
  const lastIdx = months.length - 1;
  const showAdded = !isFlow && view === 'added';
  const deltas = values.map((v, i) => (i === 0 ? null : v - values[i - 1]));
  const chartValues = showAdded ? deltas : values;
  const data = months.map((m, i) => ({ month: monthLabel(m, true), value: chartValues[i] }));
  // Flows: the current month is still running, so growth is measured on complete months only.
  const rate = cmgr(isFlow ? values.slice(0, -1) : values);

  return (
    <Section title="Growth — last 12 months">
      {/* Even columns: 2 → 3 → 5 → 9 across, so no tile ends up alone or stretched. */}
      <Box sx={{
        display: 'grid', gap: 1.5, mb: 2,
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))', xl: `repeat(${metrics.length}, minmax(0, 1fr))` },
      }}>
        {metrics.map((m) => (
          <Box key={m.key} sx={{ minWidth: 0 }}>
            <GrowthTile
              metric={m}
              series={growth.series[m.key]}
              months={months}
              selected={m.key === metric.key}
              onClick={() => { setSelectedKey(m.key); setView('total'); }}
            />
          </Box>
        ))}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Typography data-testid="growth-chart-title" sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
              {metric.label}{showAdded ? ' — added each month' : ''}
            </Typography>
            {series.source === 'sample' && <SampleChip />}
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {rate == null ? '' : `Avg monthly growth ${rate >= 0 ? '+' : ''}${rate.toFixed(1)}%`}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {!isFlow && ['total', 'added'].map((v) => (
              <Chip
                key={v}
                size="small"
                label={v === 'total' ? 'Total' : 'Added each month'}
                color={view === v ? 'primary' : 'default'}
                onClick={() => setView(v)}
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            ))}
          </Box>
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={64} tickFormatter={(v) => (metric.format === 'bytes' ? formatBytes(v) : formatCount(v))} />
                <ChartTooltip formatter={(v) => (showAdded ? fmtDelta(metric, v) : fmt(metric, v))} />
                <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                  {data.map((d, i) => (
                    <Cell key={d.month} fill={i === lastIdx && isFlow ? '#9FC5C1' : '#2E7D73'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
            UAE calendar months.{isFlow ? ' The light bar is this month so far.' : ' Each bar is the total at month end; this month is today.'}
            {metric.key === 'centers' ? ' Counted from each center’s created date.' : ''}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <TableContainer sx={{ maxHeight: 320, overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Month', metric.label, 'Change', '%'].map((h, i) => (
                    <TableCell key={h} align={i ? 'right' : 'left'} sx={{ fontSize: '0.72rem', fontWeight: 600, py: 0.75, whiteSpace: 'nowrap' }}>
                      {i === 1 ? (isFlow ? 'In month' : 'At month end') : h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...months].reverse().map((m) => {
                  const i = months.indexOf(m);
                  const prev = i > 0 ? values[i - 1] : null;
                  const pctChange = prev ? Math.round(((values[i] - prev) / prev) * 100) : null;
                  const partial = i === lastIdx && isFlow;
                  return (
                    <TableRow key={m} data-testid={`growth-row-${m}`}>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.5, whiteSpace: 'nowrap' }}>
                        {monthLabel(m)}{partial ? ' (so far)' : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5 }}>{fmt(metric, values[i])}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5, color: partial ? 'text.disabled' : undefined }}>
                        {fmtDelta(metric, deltas[i])}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5, fontWeight: 600, color: partial ? 'text.disabled' : pctColor(pctChange) }}>
                        {fmtPct(pctChange)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Section>
  );
};

export default GrowthPanel;
